import React, {useState, useEffect} from "react";
import TrixTextEditor from "../ISR/Components/TrixTextEditor";
import Tooltip from "../../../Components/GMS/Rules/Components/Tooltip";
import {InfoIcon} from "../../../assests/GmsIcons";
import ToggleButton from "../../../Components/Common/components/Inputs/ToggleButton";
import {HelpTextData} from "../ISR/Pages/DefineModel/model";

type HelpTextProps = {
    id: string;
    savedHelpTextData?: HelpTextData | null | undefined
    selectedItemId: string | null | undefined
    attachmentUrl: string
    showDescription?: boolean
    onDelete?: () => void;
    onClose?: () => void;
    onDone?: (helpTextData: HelpTextData) => void;
    onSubmit?: (helpTextData: HelpTextData) => void;
}

const HelpText = ({id, showDescription = false, savedHelpTextData, selectedItemId, attachmentUrl, onDelete, onClose, onDone, onSubmit}: HelpTextProps) => {

    const defaultInfo = {
        info: '',
        display: 'outside',
        trigger: 'hover',
        size: 'auto',
        openInNewWindow: false
    }

    const [helpTextData, setHelpTextData] = useState<any>(defaultInfo);

    const [loading, setLoading] = useState<boolean>(true);

    const updateInfo = (info: string) => {
        setHelpTextData ((prevInfo: any) => {
            return {
                ...prevInfo,
                info: info
            }
        })
    }

    useEffect(() => {
        if(savedHelpTextData) {
            setHelpTextData({
                info: savedHelpTextData.info ?? '',
                display: savedHelpTextData.display ?? 'outside',
                trigger: savedHelpTextData.trigger ?? 'hover',
                size: savedHelpTextData.size ?? 'auto',
                openInNewWindow: savedHelpTextData.openInNewWindow ?? false,
                description: savedHelpTextData.description ?? ''
            });
        } else {
            setHelpTextData(defaultInfo);
        }
        setLoading(false)
    }, [savedHelpTextData]);

    const handleValueChange = (fieldName: string, value: string) => {
        const temp = {...helpTextData}
        temp[fieldName] = value;
        setHelpTextData(temp)
    }

    const handleOpenInNewWindowChange = (value: boolean) => {
        const temp = {...helpTextData}
        temp['openInNewWindow'] = value;
        setHelpTextData(temp)
    };

    const dropDownUi = (title: string, hint: string, values: string[], defaultValue: string) => {
        const fieldName = title.toLowerCase();
        return <div className='tw-items-center tw-me-[16px]'>
            <div className="tw-flex-auto tw-flex tw-items-center tw-mb-2">
                <div
                    className="tw-text-slate-500 tw-text-sm tw-font-normal tw-font-['Noto Sans'] tw-me-[5px]">{title}</div>
                <div className="tw-me-[5px]">
                    <Tooltip text={hint}>
                        <InfoIcon/>
                    </Tooltip>
                </div>
            </div>
            <select className="drop-down-dm tw-w-[153px] tw-py-1 tw-h-[36px] tw-capitalize" value={helpTextData[fieldName] ?? defaultValue}
                    onChange={(e) => handleValueChange(fieldName, e.target.value)}>
                {values.map((item, index) => {
                    return (
                        <option value={item} key={item + index} className={'tw-capitalize'}>
                            {item}
                        </option>);
                })}
            </select>
        </div>;
    }

    const displayHint = 'Option 1: Inside - This selection makes the \'i\' icon to place within the chip\n\nOption 2: Outside - This selection makes the \'i\' icon to place on the top right of the chip';
    const triggerHint = 'Option 1: Hover - This selection makes on-mouse hover over \'i\' icon to view the info tool tip\n\nOption 2: Click - In this selection user need to click \'i\' icon to view the info tool tip';
    const sizeHint = 'Option 1: Auto - This selection automatically fits the tool tip window as per the help text content. User can drag the window to any size.\n' +
        '\n' +
        'Option 2: Small - This selection provides the small size window, user can drag the window to view the help text content.\n' +
        '\n' +
        'Option 3: Medium - This selection provides the medium size window, user can drag the window to view the help text content.\n' +
        '\n' +
        'Option 4: Large - This selection provides the large size window, user can drag the window to view the help text content.';
    return(
        <div id={id}>
            {showDescription ? <>
                <div id={`${id}-description-label`} className="tw-text-slate-500 tw-text-sm tw-leading-tight tw-mt-3">
                    Description
                </div>
                <textarea
                    id={`${id}-description-input`}
                    className="tw-w-full tw-relative tw-bg-white tw-rounded tw-border tw-border-zinc-200 tw-mt-1 tw-p-2"
                    value={helpTextData?.description ?? ''}
                    onChange={(e) => handleValueChange('description', e.target.value)}
                    placeholder={'Enter the Description'}/></> : null}
            <div className='tw-flex tw-items-center tw-mb-[32px] tw-mt-3'>
                {dropDownUi('Display', displayHint, ['outside', 'inside'], 'outside')}
                {dropDownUi('Trigger', triggerHint, ['hover', 'click'], 'hover')}
                {dropDownUi('Size', sizeHint, ['auto', 'small', 'medium', 'large'], 'auto')}
                <div className="tw-w-auto">
                    <div className="tw-flex-auto tw-flex tw-items-center tw-mb-2">
                        <div className="tw-text-slate-500 tw-text-sm tw-font-normal tw-font-['Noto Sans'] tw-me-[5px]">Open in new window</div>
                        <div className="tw-me-[5px]">
                            <Tooltip text={'Option at runtime to open the help text in a new window. This can be done by specifying a hyperlink in the help text (e.g Read More)'}>

                                <InfoIcon/>
                            </Tooltip>
                        </div>
                    </div>
                    <ToggleButton
                        id={`${id}-new-window-toggle`}
                        name="OpenInNewWindow"
                        value={helpTextData['openInNewWindow'] ?? false}
                        onChange={(e) => handleOpenInNewWindowChange(e.target.checked)}
                    />
                </div>
            </div>

            <div id={`${id}-editor-container`} className="tw-mt-3">
                {!loading && 
                    <TrixTextEditor 
                        id={`${id}-text-editor`}
                        updateInfo={updateInfo} 
                        value={helpTextData?.info ?? "<div></div>"}
                        selectedItemId={selectedItemId}
                        attachmentUrl={attachmentUrl}
                    />
                }
            </div>

            <div id={`${id}-actions`} className={`tw-my-4 tw-flex tw-justify-between`}>
                {onDelete ? 
                    <button 
                        id={`${id}-delete-btn`}
                        onClick={onDelete} 
                        className='samll-outline-btn-red' 
                        disabled={!savedHelpTextData}
                    >
                        Delete
                    </button> 
                : null}
                {onDone ? 
                    <button 
                        id={`${id}-done-btn`}
                        onClick={() => onDone(helpTextData)} 
                        className={`small-filled-btn`}
                    >
                        Done
                    </button> 
                : null}
                <div id={`${id}-right-actions`} className='tw-flex tw-gap-2 tw-justify-end'>
                    {onClose ? 
                        <button 
                            id={`${id}-cancel-btn`}
                            onClick={onClose} 
                            className='samll-outline-btn'
                        >
                            Cancel
                        </button> 
                    : null}
                    {onSubmit ? 
                        <button 
                            id={`${id}-save-btn`}
                            onClick={() => onSubmit(helpTextData)} 
                            className={`small-filled-btn`}
                        >
                            Save
                        </button> 
                    : null}
                </div>
            </div>
        </div>
    );
}

export default HelpText;